import React from 'react';
import { observer } from '../../../shared/observer';

// Платежные методы — порт client_app/src/screens/Cart/OrderForm
// (раздел `paymentMethodSection`).
//
// Контракт: orderStore.order.pay_types_rich = [{id, title, system_name}]
// где system_name ∈ {card, cash, terminal, bank_account, sbp}.
// Online-оплата (card/sbp) включает доп. шаги (выбор карты, переход на платёжку).

export const PAY_SYS = {
  CARD: 'card',
  CASH: 'cash',
  TERMINAL: 'terminal',
  BANK_ACCOUNT: 'bank_account',
  SBP: 'sbp',
};

export function findPayType(order, id) {
  return (order.pay_types_rich || []).find((p) => p.id === id) || null;
}

export function isOnlinePayment(order, id) {
  const pt = findPayType(order, id);
  return !!pt && (pt.system_name === PAY_SYS.CARD || pt.system_name === PAY_SYS.SBP);
}

export function isCardPayment(order, id) {
  const pt = findPayType(order, id);
  return !!pt && pt.system_name === PAY_SYS.CARD;
}

// SVG-иконки методов оплаты (минималистичные feather-style line icons).
const ICON = {
  [PAY_SYS.CARD]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  [PAY_SYS.CASH]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2" />
      <path d="M6 10h.01M18 14h.01" />
    </svg>
  ),
  [PAY_SYS.TERMINAL]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="8" y1="14" x2="8" y2="14.01" />
      <line x1="12" y1="14" x2="12" y2="14.01" />
      <line x1="16" y1="14" x2="16" y2="14.01" />
    </svg>
  ),
  [PAY_SYS.BANK_ACCOUNT]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M3 9l9-6 9 6" />
      <path d="M4 10v10h16V10" />
      <line x1="9" y1="12" x2="9" y2="18" />
      <line x1="15" y1="12" x2="15" y2="18" />
    </svg>
  ),
  [PAY_SYS.SBP]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 8l8 8M8 16l8-8" />
    </svg>
  ),
};

class PayTypeView extends React.Component {
  _defaultsApplied = false;

  componentDidMount() {
    this.tryApplyDefaults();
    if (!this.props.card.cards || this.props.card.cards.length === 0) {
      this.props.card.getCards().catch(() => { /* silent */ });
    }
  }

  componentDidUpdate() {
    // observer-обёртка инжектирует одну и ту же ссылку на orderStore, поэтому
    // prev/curr props не различаются. Используем флаг + проверку контекста.
    this.tryApplyDefaults();
  }

  tryApplyDefaults() {
    if (this._defaultsApplied) return;
    const order = this.props.order.order || {};
    const list = order.pay_types_rich || [];
    if (!list.length) return;
    // Способ, выбранный ранее нами или присланный сервером в order.pay_type_id
    // (= default_pay_type), используем только если он реально есть в списке
    // выбираемых. Иначе — первый. Иначе при невыбираемом дефолте подсветка
    // пропадала: selectedId указывал на отсутствующий чип (см. скрин «не видно
    // какой способ выбран»), хотя заказ всё равно оформлялся.
    const current = this.props.order.fieldsToUpdateOrder.pay_type_id || order.pay_type_id;
    const valid = current && list.some((p) => p.id === current);
    this.applyForId(valid ? current : list[0].id);
    this._defaultsApplied = true;
  }

  applyForId(payTypeId) {
    const order = this.props.order.order || {};
    const pt = findPayType(order, payTypeId);
    if (!pt) return;
    const cards = this.props.card.cards || [];
    if (pt.system_name === PAY_SYS.CARD && cards.length) {
      this.props.order.setFieldsToUpdateOrder({ pay_type_id: pt.id, card_id: cards[0].id });
    } else {
      this.props.order.setFieldsToUpdateOrder({ pay_type_id: pt.id });
    }
  }

  pickPayType = (id) => () => {
    // props.disabled — пока PaymentForm отправляет заказ, менять способ нельзя.
    if (this.props.disabled) return;
    const order = this.props.order.order || {};
    const pt = findPayType(order, id);
    const patch = { pay_type_id: id };
    if (pt && pt.system_name === PAY_SYS.CARD) {
      const cards = this.props.card.cards || [];
      if (cards.length) patch.card_id = cards[0].id;
    } else {
      patch.card_id = '';
    }
    this.props.order.setFieldsToUpdateOrder(patch);
  };

  pickCard = (id) => () => {
    if (this.props.disabled) return;
    this.props.order.setFieldsToUpdateOrder({ card_id: id || '' });
  };

  render() {
    const order = this.props.order.order || {};
    const list = order.pay_types_rich || [];
    if (!list.length) return null;

    const selectedId = this.props.order.fieldsToUpdateOrder.pay_type_id || order.pay_type_id || '';
    const cardSelectedId = this.props.order.fieldsToUpdateOrder.card_id;
    const cards = this.props.card.cards || [];
    const cardSection = isCardPayment(order, selectedId) && cards.length > 0;

    return (
      <section className="ng-checkout__section">
        <label>Способ оплаты</label>
        <div className="ng-pay-types">
          {list.map((pt) => (
            <button
              key={pt.id}
              type="button"
              className={'ng-pay-type' + (pt.id === selectedId ? ' ng-pay-type--active' : '')}
              disabled={!!this.props.disabled}
              onClick={this.pickPayType(pt.id)}
            >
              {ICON[pt.system_name] && <span className="ng-pay-type__icon">{ICON[pt.system_name]}</span>}
              <span className="ng-pay-type__title">{pt.title}</span>
            </button>
          ))}
        </div>

        {cardSection && (
          <div className="ng-pay-cards">
            <div className="ng-pay-cards__label">Карта</div>
            {cards.map((card) => (
              <button
                key={card.id}
                type="button"
                className={'ng-pay-card' + (card.id === cardSelectedId ? ' ng-pay-card--active' : '')}
                disabled={!!this.props.disabled}
                onClick={this.pickCard(card.id)}
              >
                <span className="ng-pay-card__icon">{ICON[PAY_SYS.CARD]}</span>
                <span className="ng-pay-card__title">{card.title || card.num_id || card.pan}</span>
              </button>
            ))}
            <button
              type="button"
              className={'ng-pay-card' + (!cardSelectedId ? ' ng-pay-card--active' : '')}
              disabled={!!this.props.disabled}
              onClick={this.pickCard('')}
            >
              <span className="ng-pay-card__icon">{ICON[PAY_SYS.CARD]}</span>
              <span className="ng-pay-card__title">Оплатить новой картой</span>
            </button>
          </div>
        )}
      </section>
    );
  }
}

export const PayType = observer(PayTypeView, ['orderStore', 'cardStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/Checkout/PayType.jsx